// typescript
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {TranslateService} from '@ngx-translate/core';
import {AlertService} from '../../../services/general/alert.service';
import {SurveyService} from '../../../services/surveys.service';
import {ToastService} from '../../../services/general/toast.service';
import {ModalEmailResponsesComponent} from '../../../modals/modal-email-responses/modal-email-responses.component';
import {MatDialog} from '@angular/material/dialog';
import {HelperService} from '../../../services/general/helper.service';
import {EChartsOption} from 'echarts';
import {faBullhorn, faEnvelope, faTimes} from '@fortawesome/free-solid-svg-icons';
import {faPlusCircle} from '@fortawesome/free-solid-svg-icons/faPlusCircle';
import {faDownload} from '@fortawesome/free-solid-svg-icons/faDownload';
import {faTrash, faEdit, faLink} from '@fortawesome/free-solid-svg-icons';

interface TeamMember {
  id: number | string;
  email: string;
  name: string;
  response_status: string;
  last_status_invitation_email: string;
  invitation_link: string;
}

@Component({
  selector: 'app-survey-team',
  templateUrl: './survey-team.component.html',
  styleUrls: ['./survey-team.component.scss']
})
export class SurveyTeamComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() members: TeamMember[] = [];
  @Input() surveyId: number | string | null = null;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<TeamMember>([]);
  selectedEmailIds = new Set<number | string>();
  displayedColumns = ['name', 'email', 'response_status', 'email_status'];
  singleTeamMemberToAddActive = false;
  loading = false;
  emailStatuses = [];

  filters = {
    responseStatus: 'ALL',
    emailStatus: [],
    searchValue: '',
  };

  chartOptions!: EChartsOption;

  readonly faEnvelope = faEnvelope;
  readonly faBullhorn = faBullhorn;
  readonly faPlusCircle = faPlusCircle;
  readonly faTimes = faTimes;
  readonly faDownload = faDownload;
  readonly faTrash = faTrash;
  readonly faEdit = faEdit;
  readonly faLink = faLink;

  constructor(
    private translateService: TranslateService,
    private alert: AlertService,
    private surveyService: SurveyService,
    private toast: ToastService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.initDataSource();
    this.renderChart();
    this.emailStatuses = Array.from(
      new Set(
        (this.members || [])
          .map(m => m.last_status_invitation_email)
          .filter(e => !!e)
      )
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.members) {
      this.dataSource.data = this.members || [];
      this.renderChart();
    }
  }

  private initDataSource(): void {
    this.dataSource = new MatTableDataSource<TeamMember>(this.members || []);
    this.dataSource.filterPredicate = this.createFilterPredicate();
  }

  applyFilter() {
    this.dataSource.filter = JSON.stringify({
      status: this.filters.responseStatus.toUpperCase() || 'ALL',
      search: this.filters.searchValue,
      emailStatus: this.filters.emailStatus || []
    });
  }

  toggleAddNewTeamMemberMode(): void {
    this.singleTeamMemberToAddActive = !this.singleTeamMemberToAddActive;
  }

  onAddTeamMember(teamMember: TeamMember): void {
    this.toggleAddNewTeamMemberMode();
    this.members.push(teamMember);
    this.dataSource.data = this.members;
    this.renderChart();
  }

  resendInvitation(list, success_callback = (data: any) => {
  }): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant('components.decision-table.show-decision-table.alert.resend-invitation'),
      (ok: boolean) => {
        if (!ok) {
          return;
        }
        this.surveyService.resendInvitations(this.surveyId, list, (result) => {
          this.toast.success(this.translateService.instant('components.decision-table.show-decision-table.alert.invitation-sent'));
          success_callback(result);
        });
      }
    );
  }

  copyLink(link: string): void {
    navigator.clipboard.writeText(link);
    this.toast.success(this.translateService.instant('generic.success'), this.translateService.instant('generic.link-copied'));
  }

  deleteTeamMembers(list, success_callback = (data: any) => {
  }): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant('components.surveys.survey-team.survey-team.remove-team-member-alert'),
      (ok: boolean) => {
        if (!ok) {
          return;
        }
        this.loading = true;
        this.surveyService.deleteTeamMembers(this.surveyId, list, (result) => {
          this.loading = false;
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.surveys.survey-team.survey-team.remove-team-member-alert-successfully'));
          this.members = this.members.filter((member) => list.includes(member.id));
          this.dataSource.data = this.members;
          this.renderChart();
          success_callback(result);
        }, (error: any) => {
          this.loading = false;
        });
      }
    );
  }

  showEmailResponses(row: TeamMember | null): void {
    if (!row.last_status_invitation_email) {
      return;
    }
    this.dialog.open(ModalEmailResponsesComponent, {
      panelClass: 'medium-dialog',
      data: {team_member_id: row.id, origin_id: this.surveyId, origin: 'SURVEY'}
    });
  }

  emailColorByTeamMember(row: TeamMember | null): string {
    if (row.last_status_invitation_email) {
      return HelperService.getColorByEmailEvent(row.last_status_invitation_email);
    }
    return '';
  }

  downloadSurveyTeamDataCSV(): void {
    this.loading = true;
    const payload = Array.from(this.selectedEmailIds);
    this.surveyService.downloadSurveyTeamData(this.surveyId, payload, () => {
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  isEmailSelected(row: TeamMember | null): boolean {
    return !!row && this.selectedEmailIds.has(row.id);
  }

  resendSelectedInvitations(): void {
    if (!this.dataSource || this.selectedEmailIds.size === 0) {
      return;
    }
    const ids = Array.from(this.selectedEmailIds);
    this.resendInvitation(ids, () => {
      this.selectedEmailIds = new Set();
      this.cdr.markForCheck();
    });
  }

  deleteSelectedMembers(): void {
    if (!this.dataSource || this.selectedEmailIds.size === 0) {
      return;
    }
    if (this.members?.length < 2) {
      return;
    }
    const ids = Array.from(this.selectedEmailIds);
    this.deleteTeamMembers(ids, () => {
      this.selectedEmailIds = new Set();
      this.cdr.markForCheck();
    });
  }

  isAllSelected(): boolean {
    const filtered = this.filteredRows();
    return filtered.length > 0 && filtered.every(row => this.selectedEmailIds.has(row.id));
  }

  isSomeSelected(): boolean {
    const filtered = this.filteredRows();
    const selectedCount = filtered.filter(row => this.selectedEmailIds.has(row.id)).length;
    return selectedCount > 0 && selectedCount < filtered.length;
  }

  toggleSelectEmail(row: TeamMember, event: { checked: boolean }): void {
    const next = new Set(this.selectedEmailIds);
    if (event.checked) {
      next.add(row.id);
    } else {
      next.delete(row.id);
    }
    this.selectedEmailIds = next;
    this.cdr.markForCheck();
  }

  toggleSelectAll(event: { checked: boolean }): void {
    if (event.checked) {
      this.selectedEmailIds = new Set(this.filteredRows().map(r => r.id));
    } else {
      this.selectedEmailIds = new Set();
    }
    this.cdr.markForCheck();
  }

  filteredRows(): TeamMember[] {
    return (this.dataSource && this.dataSource.filteredData) ? this.dataSource.filteredData : this.dataSource.data;
  }

  createFilterPredicate(): (data: TeamMember, filter: string) => boolean {
    return (data: TeamMember, filter: string): boolean => {
      const { status, search, emailStatus } = JSON.parse(filter || '{}');

      const normalizedStatus = (status || 'ALL').toUpperCase();
      const normalizedSearch = (search || '').trim().toLowerCase();
      const selectedEmailStatuses: string[] = emailStatus || [];

      // 1) filtro STATUS
      if (normalizedStatus !== 'ALL' && data.response_status !== normalizedStatus) {
        return false;
      }

      // 2) filtro EMAIL STATUS
      if (selectedEmailStatuses.length > 0) {
        const memberEmailStatus = data.last_status_invitation_email;
        if (!memberEmailStatus || !selectedEmailStatuses.includes(memberEmailStatus)) {
          return false;
        }
      }

      // 3) filtro SEARCH
      if (normalizedSearch) {
        const searchable = [data.name, data.email];
        const match = searchable
          .filter(v => !!v)
          .some(v => String(v).toLowerCase().includes(normalizedSearch));
        if (!match) { return false; }
      }

      return true;
    };
  }

  private renderChart(): void {
    const members = this.members || [];
    const answeredCount = members.filter(m =>
      m.response_status === 'SENT'
    ).length;
    const draftCount = members.filter(m =>
      m.response_status === 'DRAFT'
    ).length;
    const notAnsweredCount = members.length - answeredCount - draftCount;

    this.chartOptions = {
      tooltip: {trigger: 'item'},
      legend: {orient: 'vertical', left: 'left'},
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: '50%',
          data: [
            {
              value: answeredCount,
              name: this.translateService.instant('generic.who-has-responded'),
              itemStyle: {color: '#4caf50'}
            },
            {
              value: notAnsweredCount,
              name: this.translateService.instant('generic.who-has-not-responded'),
              itemStyle: {color: '#f44336'}
            },
            {
              value: draftCount,
              name: this.translateService.instant('generic.answers.draft'),
              itemStyle: {color: '#bca900'}
            },
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  }

  emailResponsesAvailable(row): boolean {
    return row.last_status_invitation_email !== 'not-yet-sent';
  }
}
